import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import useAuth from "../../hooks/useAuth";
import { canViewDrinks, canViewInstantMenu, getDefaultRouteForPermissions } from "../../utils/permissions";

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requiredPermission?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
}) => {
  const { isAuthenticated, loading, admin } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredPermission) {
    const hasPermission = requiredPermission === "drinks" ? canViewDrinks(admin) :
      requiredPermission === "instantMenu" ? canViewInstantMenu(admin) : admin?.permissions?.[requiredPermission];

    if (!hasPermission) {
      const fallbackPath = getDefaultRouteForPermissions(admin?.permissions);
      return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }
  }

  // Render child routes if authenticated
  if (children !== undefined) {
    return <>{children}</>;
  }

  return <Outlet />;
};

export default ProtectedRoute;
